"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formUnion = exports.collapseUnion = exports.mergeTypes = exports.areUnionsCompatible = exports.areUnionsEquivalent = exports.areTypesEquivalent = exports.isArrayType = exports.isUnionType = exports.isBooleanType = exports.isStringType = exports.isNumberType = exports.isPrimitiveType = void 0;
var type_1 = require("./type");
var checker_1 = require("./checker");
/**
 * Type guard that checks if the given type is a primitive (a string, number, or boolean).
 */
function isPrimitiveType(type) {
    return isNumberType(type) || isStringType(type) || isBooleanType(type);
}
exports.isPrimitiveType = isPrimitiveType;
/**
 * Type guard that checks if the given type is a number type.
 */
function isNumberType(type) {
    return typeof type === "object" && type.type === "number";
}
exports.isNumberType = isNumberType;
/**
 * Type guard that checks if the given type is a string type.
 */
function isStringType(type) {
    return typeof type === "object" && type.type === "string";
}
exports.isStringType = isStringType;
/**
 * Type guard that checks if the given type is a boolean type.
 */
function isBooleanType(type) {
    return typeof type === "object" && type.type === "boolean";
}
exports.isBooleanType = isBooleanType;
/**
 * Type guard that checks if the given type is a union.
 */
function isUnionType(type) {
    return typeof type === "object" && type["type"] === "union";
}
exports.isUnionType = isUnionType;
/**
 * Type guard that checks if the given type is an array type.
 */
function isArrayType(type) {
    return (typeof type === "string" && type === type_1.AnyArrayType) || (typeof type === "object" && type["type"] === type_1.AnyArrayType);
}
exports.isArrayType = isArrayType;
/**
 * Checks if two types are equivalent.
 *
 * Primitive types are considered equivalent if they're exactly the same.
 *
 * Array types are considered equivalent if their {@link ArrayType.elements element types} are equivalent. This means
 * {@link AnyArrayType} is only equivalent to itself.
 *
 * Union types are considered equivalent if they have the same options. See {@link areUnionsEquivalent} for more details
 * about union type equivalence.
 *
 * @param a The first type.
 * @param b The second type.
 * @returns True if the types are equivalent.
 */
function areTypesEquivalent(a, b) {
    if (typeof a === "string") {
        return a === b;
    }
    else if (isStringType(a)) {
        return isStringType(b) && a.value === b.value;
    }
    else if (isNumberType(a)) {
        return isNumberType(b) && a.value === b.value;
    }
    else if (isBooleanType(a)) {
        return isBooleanType(b) && a.value === b.value;
    }
    else if (isUnionType(a)) {
        return isUnionType(b) && areUnionsEquivalent(a, b);
    }
    else if (isArrayType(a)) {
        return isArrayType(b) && areTypesEquivalent(a.elements, b.elements);
    }
    else {
        throw new Error("Unhandled type " + JSON.stringify(a));
    }
}
exports.areTypesEquivalent = areTypesEquivalent;
/**
 * Checks if two unions have equivalent sets of options. If `a` has options that `b` does not, or vice versa, the unions
 * are not equivalent (although they are overlapping).
 *
 * Before comparing the option sets, the unions are {@link collapseUnion collapsed} (the original types passed in are
 * not modified).
 *
 * Union options are considered to be sets, meaning order is not considered when comparing them.
 *
 * @param a The first union type.
 * @param b The second union type.
 * @returns True if the union types have equivalent options.
 */
function areUnionsEquivalent(a, b) {
    // Collapse unions before comparing.
    // Calling collapseUnion is defined create a new type, so we can modify the options array freely.
    var aOptions = collapseUnion(a).options;
    var bOptions = collapseUnion(b).options;
    var _loop_1 = function (bOpt) {
        var index = aOptions.findIndex(function (aOpt) { return areTypesEquivalent(aOpt, bOpt); });
        if (index === -1) {
            return { value: false };
        }
        else {
            aOptions.splice(index, 1);
        }
    };
    for (var _i = 0, bOptions_1 = bOptions; _i < bOptions_1.length; _i++) {
        var bOpt = bOptions_1[_i];
        var state_1 = _loop_1(bOpt);
        if (typeof state_1 === "object")
            return state_1.value;
    }
    // if there are any options from A remaining in the array, that means B did not have them, therefore they're not equivalent
    return aOptions.length === 0;
}
exports.areUnionsEquivalent = areUnionsEquivalent;
/**
 * Checks if a candidate union type is compatible with (i.e. not wider than) another union type.
 *
 * For example: `string | number` is compatible with `string | number | boolean`, but not vice versa.
 *
 * @param candidate The union type that should be narrower or the same as the other type.
 * @param type The union type that should be wider than the candidate.
 * @returns If all of the options in the candidate type are contained in the other type.
 */
function areUnionsCompatible(candidate, type) {
    return candidate.options.every(function (candidateOption) {
        return type.options.some(function (typeOption) {
            // Only check that the primitive values match if the wider type specifies a value.
            // If only the narrower type specifies a value (e.g., because it's been inferred from a literal), it can be
            // implicitly widened to the full primitive type.
            var matchValues = (isStringType(typeOption) || isNumberType(typeOption) || isBooleanType(typeOption)) && typeOption.hasOwnProperty('value');
            return checker_1.typeMatchesType(candidateOption, typeOption, matchValues);
        });
    });
}
exports.areUnionsCompatible = areUnionsCompatible;
/**
 * Reduces an array of types into the smallest number of unique options.
 *
 * For example, `mergeTypes([StringType, StringType])` produces `[StringType]`.
 *
 * If there are multiple primitive types of specific values, they will be combined into a single value-less primitive type.
 * For example, `mergeTypes([{type: "number", value: 1}, {type: "number", value: 2}])` produces `[NumberType]`.
 *
 * Nested union options will also be merged. For example, `mergeTypes([{type: "union", options: [StringType, {type: "union", options: [NumberType, BooleanType]}]}])`
 * produces `[{type: "union", options: [StringType, NumberType, BooleanType]}]`.
 */
function mergeTypes(types) {
    if (types.length <= 1)
        return types;
    var uniqueTypes = [];
    var alreadyHasType = function (type) {
        return uniqueTypes.some(function (other) {
            return areTypesEquivalent(type, other);
        });
    };
    var add = function (option) {
        if (isPrimitiveType(option)) {
            var index = uniqueTypes.findIndex(function (other) { return isPrimitiveType(other) && other.type === option.type; });
            if (index >= 0) {
                var existing = uniqueTypes[index];
                if (existing.value !== undefined) {
                    uniqueTypes.splice(index, 1);
                    uniqueTypes.push({ type: option.type });
                }
            }
            else {
                uniqueTypes.push(option);
            }
        }
        else if (isUnionType(option)) {
            option.options.forEach(add);
        }
        else if (!alreadyHasType(option)) {
            uniqueTypes.push(option);
        }
    };
    types.forEach(add);
    return uniqueTypes;
}
exports.mergeTypes = mergeTypes;
/**
 * This function takes a union type and collapses any nested unions (recursively) into a single
 * top-level union type.
 *
 * For example, when given a union type whose options are `[StringType, {type: "union", options: [NumberType, BooleanType]}]`, this
 * function will return a new union type with the options `[StringType, NumberType, BooleanType]`.
 *
 * @param type The type to collapse.
 * @returns A new union type containing the collapsed set of options from the input union.
 * @see mergeTypes
 */
function collapseUnion(type) {
    return { type: "union", options: mergeTypes(type.options) };
}
exports.collapseUnion = collapseUnion;
/**
 * Creates a union type by merging two types.
 * @see mergeTypes
 */
function formUnion(a, b) {
    return { type: "union", options: mergeTypes([a, b]) };
}
exports.formUnion = formUnion;
//# sourceMappingURL=utils.js.map